import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import MermaidEditor from '../mermaid/MermaidEditor';

/**
 * Reusable modal for adding new diagrams (process flows, architecture, etc.)
 * with a title field and Mermaid editor
 */
const AddDiagramModal = ({
  open,
  onClose,
  onSave,
  title = 'Add New Diagram',
  titleLabel = 'Diagram Title',
  titlePlaceholder = 'Enter diagram title',
  descriptionLabel = 'Description',
  descriptionPlaceholder = 'Enter diagram description',
  initialTitle = '',
  initialDescription = '',
  initialCode = ''
}) => {
  const [diagramTitle, setDiagramTitle] = useState(initialTitle);
  const [diagramDescription, setDiagramDescription] = useState(initialDescription);

  const handleSave = (mermaidCode) => {
    if (onSave) {
      onSave(mermaidCode, diagramTitle, diagramDescription);
    }
  };

  const handleClose = () => {
    setDiagramTitle('');
    setDiagramDescription('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '75vw',
          height: '90vh',
          maxWidth: 'none',
          maxHeight: 'none',
          m: 1,
          borderRadius: '4px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.1)'
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: '0px',
          pt: '14px',
          pl: 5,
          pr: '10px',
          background: 'white',
          border: '0 !important'
        }}
      >
        <Box
          component="span"
          sx={{
            color: '#485869',
            fontSize: '1.1rem',
            fontWeight: 600,
            fontFamily: 'inherit'
          }}
        >
          {title}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          border: '0 !important',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          bgcolor: 'white'
        }}
      >
        <Box>
          <Typography
            variant="body1"
            sx={{
              mb: 1,
              fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
              fontWeight: 700,
              fontSize: '14px',
              color: '#475969'
            }}
          >
            {titleLabel} <span style={{ color: '#F89939' }}>*</span>
          </Typography>
          <TextField
            value={diagramTitle}
            onChange={(e) => setDiagramTitle(e.target.value)}
            fullWidth
            placeholder={titlePlaceholder}
            autoFocus
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#485869',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#485869',
                  borderWidth: '1px',
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4759694d',
                border: '1px solid #4759694d',
                borderRadius: '4px',
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
                padding: '12px 14px',
                color: 'var(--color-blue, #475969)',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#a2abb3',
                opacity: 0.7,
                fontSize: '14px',
              },
            }}
          />
        </Box>
        <Box>
          <Typography
            variant="body1"
            sx={{
              mb: 1,
              fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
              fontWeight: 700,
              fontSize: '14px',
              color: '#475969'
            }}
          >
            {descriptionLabel}
          </Typography>
          <TextField
            value={diagramDescription}
            onChange={(e) => setDiagramDescription(e.target.value)}
            fullWidth
            placeholder={descriptionPlaceholder}
            variant="outlined"
            multiline
            rows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#485869',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#485869',
                  borderWidth: '1px',
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4759694d',
                border: '1px solid #4759694d',
                borderRadius: '4px',
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
                padding: '12px 14px',
                color: 'var(--color-blue, #475969)',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#a2abb3',
                opacity: 0.7,
                fontSize: '14px',
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <MermaidEditor
            initialCode={initialCode}
            height="100%"
            onSave={handleSave}
            onCancel={handleClose}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddDiagramModal;
